import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SearchOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import DiagnosisCard from 'components/Diagnosis';
import PatientLogHeader from './PatientLogHeader';
import NewDiagnose from 'components/forms/Diagnoses/NewDiagnose';
import localeTranslator from 'library/Hocs/LocalTranslator';
import 'pages/ShowAppointment/components/PatientLog/style.css';
var initialsFromName = function (name) {
    if (!name)
        return '??';
    var parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length)
        return '??';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return "".concat(parts[0][0]).concat(parts[parts.length - 1][0]).toUpperCase();
};
var diagnosisTitle = function (text, fallback) {
    var firstLine = text
        .trim()
        .split(/[\n.]+/)
        .map(function (part) { return part.trim(); })
        .find(Boolean);
    if (!firstLine)
        return fallback;
    return firstLine.length > 72 ? "".concat(firstLine.slice(0, 72).trim(), "\u2026") : firstLine;
};
/** Prefer a clinical-notes body only when it adds detail beyond the title line. */
var diagnosisNotes = function (text, title) {
    var trimmed = text.trim();
    if (!trimmed)
        return undefined;
    var titleBase = title.replace(/…$/, '').trim();
    if (trimmed === titleBase)
        return undefined;
    return trimmed;
};
var getDetailFields = function (diagnose) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    var details = (_a = diagnose.getDetails) === null || _a === void 0 ? void 0 : _a.call(diagnose);
    return {
        name: ((_c = (_b = details === null || details === void 0 ? void 0 : details.getName) === null || _b === void 0 ? void 0 : _b.call(details)) === null || _c === void 0 ? void 0 : _c.trim()) || '',
        code: ((_e = (_d = details === null || details === void 0 ? void 0 : details.getCode) === null || _d === void 0 ? void 0 : _d.call(details)) === null || _e === void 0 ? void 0 : _e.trim()) || '',
        severity: ((_f = details === null || details === void 0 ? void 0 : details.getSeverity) === null || _f === void 0 ? void 0 : _f.call(details)) || '',
        conditionType: ((_g = details === null || details === void 0 ? void 0 : details.getConditionType) === null || _g === void 0 ? void 0 : _g.call(details)) || '',
        notes: ((_j = (_h = details === null || details === void 0 ? void 0 : details.getNotes) === null || _h === void 0 ? void 0 : _h.call(details)) === null || _j === void 0 ? void 0 : _j.trim()) || '',
        text: ((_k = details === null || details === void 0 ? void 0 : details.getDiagnoseText) === null || _k === void 0 ? void 0 : _k.call(details)) || '',
        resolved: Boolean((_l = details === null || details === void 0 ? void 0 : details.isResolved) === null || _l === void 0 ? void 0 : _l.call(details))
    };
};
var isRespiratory = function (diagnose) {
    var _a = getDetailFields(diagnose), code = _a.code, name = _a.name, text = _a.text;
    if (code && /^J/i.test(code))
        return true;
    var haystack = "".concat(name, " ").concat(text).toLowerCase();
    return /pharyng|respirat|throat|tonsil|bronch|pneumon|sinus|rhinitis|asthma|cough|flu|influenza/.test(haystack);
};
var isResolved = function (diagnose) { return getDetailFields(diagnose).resolved; };
var matchesSearch = function (diagnose, query) {
    if (!query)
        return true;
    var _a = getDetailFields(diagnose), name = _a.name, code = _a.code, notes = _a.notes, text = _a.text;
    var haystack = "".concat(name, " ").concat(code, " ").concat(notes, " ").concat(text).toLowerCase();
    return haystack.includes(query);
};
var Diagnoses = function (props) {
    var t = props.translate, diagnoses = props.diagnoses;
    var _a = useState(false), showDiagnoseForm = _a[0], setShowDiagnoseForm = _a[1];
    var _b = useState(''), search = _b[0], setSearch = _b[1];
    var _c = useState('all'), filter = _c[0], setFilter = _c[1];
    var goBack = function () {
        if (showDiagnoseForm) {
            setShowDiagnoseForm(false);
            return;
        }
        props.setShowDiagnoses(false);
    };
    var closeDrawer = function () { return props.setShowDiagnoses(false); };
    var addDiagnose = function () { return setShowDiagnoseForm(true); };
    var normalizedSearch = search.trim().toLowerCase();
    var counts = useMemo(function () {
        var active = diagnoses.filter(function (d) { return !isResolved(d); }).length;
        var resolved = diagnoses.filter(function (d) { return isResolved(d); }).length;
        var respiratory = diagnoses.filter(function (d) { return isRespiratory(d); }).length;
        return {
            all: diagnoses.length,
            active: active,
            resolved: resolved,
            respiratory: respiratory
        };
    }, [diagnoses]);
    var filteredDiagnoses = useMemo(function () {
        return diagnoses.filter(function (diagnose) {
            if (!matchesSearch(diagnose, normalizedSearch))
                return false;
            if (filter === 'active')
                return !isResolved(diagnose);
            if (filter === 'resolved')
                return isResolved(diagnose);
            if (filter === 'respiratory')
                return isRespiratory(diagnose);
            return true;
        });
    }, [diagnoses, filter, normalizedSearch]);
    var activeInView = filteredDiagnoses.filter(function (d) { return !isResolved(d); });
    if (showDiagnoseForm && props.createDiagnose) {
        return (_jsxs("div", { className: "patient-log", children: [_jsx(PatientLogHeader, { title: t('diagnoses.form.title'), badge: t('diagnoses.form.badge'), hasAddButton: false, hasBackButton: true, hasCloseButton: true, onGoBack: goBack, onClose: closeDrawer, arrowIcon: props.arrowIcon }), _jsx("div", { className: "patient-log__body patient-log-dx-form", children: _jsx(NewDiagnose, { createDiagnose: props.createDiagnose, setShowDiagnoseForm: setShowDiagnoseForm, patientName: props.patientName, patientCode: props.patientCode, appointmentId: props.appointmentId }) })] }));
    }
    var filters = [
        {
            id: 'all',
            label: t('diagnoses.filters.all', { count: counts.all })
        },
        {
            id: 'active',
            label: t('diagnoses.filters.active', { count: counts.active })
        },
        {
            id: 'resolved',
            label: t('diagnoses.filters.resolved', { count: counts.resolved })
        },
        {
            id: 'respiratory',
            label: t('diagnoses.filters.respiratory')
        }
    ];
    return (_jsxs("div", { className: "patient-log", children: [_jsx(PatientLogHeader, { title: t('diagnoses.title'), subtitle: t('diagnoses.subtitle'), badge: t('diagnoses.badge'), hasAddButton: Boolean(props.createDiagnose), hasBackButton: true, hasCloseButton: true, addLabel: t('diagnoses.add'), onGoBack: goBack, onClose: closeDrawer, onAdd: addDiagnose, arrowIcon: props.arrowIcon }), _jsxs("div", { className: "patient-log-dx-toolbar", children: [_jsx("div", { className: "patient-log-dx-patient", children: _jsxs("div", { className: "patient-log-dx-patient__main", children: [_jsx("div", { className: "patient-log-dx-patient__avatar", "aria-hidden": true, children: initialsFromName(props.patientName) }), _jsxs("div", { className: "patient-log-dx-patient__meta", children: [_jsxs("div", { className: "patient-log-dx-patient__name-row", children: [_jsx("span", { className: "patient-log-dx-patient__name", children: props.patientName || '—' }), props.patientCode ? (_jsxs("span", { className: "patient-log-dx-patient__code", children: ["#", props.patientCode] })) : null] }), _jsxs("div", { className: "patient-log-dx-patient__status", children: [_jsx("span", { className: "patient-log-dx-patient__status-dot" }), _jsx("span", { children: t('diagnoses.activeRecords', { count: counts.active }) })] })] })] }) }), _jsxs("div", { className: "patient-log-dx-search", children: [_jsx(SearchOutlined, { className: "patient-log-dx-search__icon", "aria-hidden": true }), _jsx("input", { type: "search", className: "patient-log-dx-search__input", value: search, onChange: function (event) { return setSearch(event.target.value); }, placeholder: t('diagnoses.searchPlaceholder'), "aria-label": t('diagnoses.searchPlaceholder') })] }), _jsx("div", { className: "patient-log-dx-filters", role: "tablist", children: filters.map(function (item) { return (_jsx("button", { type: "button", role: "tab", "aria-selected": filter === item.id, className: "patient-log-dx-filter".concat(filter === item.id ? ' is-active' : ''), onClick: function () { return setFilter(item.id); }, children: item.label }, item.id)); }) })] }), _jsx("div", { className: "patient-log__body patient-log-dx-list", "data-onboarding": "patient-log-diagnoses-section", "data-id": "diagnoses", children: diagnoses.length === 0 ? (_jsx("div", { className: "patient-log__empty", children: t('diagnoses.empty') })) : filteredDiagnoses.length === 0 ? (_jsx("div", { className: "patient-log__empty", children: t('diagnoses.noMatches') })) : (_jsxs(_Fragment, { children: [activeInView.length > 0 && (_jsxs("div", { className: "patient-log-dx-section", children: [_jsxs("div", { className: "patient-log-dx-section__label", children: [_jsx("span", { children: t('diagnoses.activeConditions') }), _jsx("span", { className: "patient-log-dx-section__count", children: activeInView.length })] }), _jsx("span", { className: "patient-log-dx-section__meta", children: t('diagnoses.currentEncounter') })] })), filteredDiagnoses.map(function (diagnose, index) {
                            var _a, _b;
                            var id = diagnose.getId();
                            var fields = getDetailFields(diagnose);
                            var fallbackTitle = t('diagnoses.cardTitle', { id: id });
                            var title = fields.name
                                ? diagnosisTitle(fields.name, fallbackTitle)
                                : diagnosisTitle(fields.text, fallbackTitle);
                            var notes = fields.notes ||
                                (!fields.name
                                    ? diagnosisNotes(fields.text, title)
                                    : undefined);
                            var appointmentId = (_a = diagnose.getAppointmentId) === null || _a === void 0 ? void 0 : _a.call(diagnose);
                            var recordedAt = (_b = diagnose.getCreatedAt) === null || _b === void 0 ? void 0 : _b.call(diagnose);
                            var showDoctor = Boolean(props.doctorName) &&
                                (!props.appointmentId ||
                                    !appointmentId ||
                                    Number(appointmentId) === Number(props.appointmentId));
                            var conditionLabel = fields.conditionType
                                ? t("diagnoses.form.conditionTypeOptions.".concat(fields.conditionType))
                                : t('diagnoses.encounterDiagnosis');
                            var metaParts = [
                                conditionLabel,
                                appointmentId
                                    ? t('diagnoses.visitLinked', { id: appointmentId })
                                    : undefined
                            ].filter(Boolean);
                            return (_jsx("div", { "data-id": "diagnose-".concat(id), children: _jsx(DiagnosisCard, { title: title, code: fields.code || undefined, severityLabel: fields.severity
                                        ? t("diagnoses.form.severityOptions.".concat(fields.severity))
                                        : undefined, severityTone: fields.severity === 'mild' ||
                                        fields.severity === 'moderate' ||
                                        fields.severity === 'severe' ||
                                        fields.severity === 'chronic'
                                        ? fields.severity
                                        : undefined, notes: notes, notesLabel: t('diagnoses.clinicalNotes'), metaLabel: metaParts.join(' · '), activeLabel: t('diagnoses.active'), doctorLabel: showDoctor ? props.doctorName : undefined, recordedAtLabel: recordedAt || undefined, accent: fields.conditionType === 'secondary'
                                        ? 'sky'
                                        : fields.conditionType === 'primary' || index === 0
                                            ? 'amber'
                                            : 'sky' }) }, "diagnose-".concat(id)));
                        })] })) }), _jsx("footer", { className: "patient-log__footer patient-log__footer--end", children: _jsx("button", { type: "button", className: "patient-log-header__btn patient-log-header__btn--ghost", onClick: closeDrawer, children: t('menu.closeDrawer') }) })] }));
};
export default localeTranslator(Diagnoses, 'patientLog');
